import { Link } from "react-router-dom";
import { BookOpen, Users, GraduationCap, Home, UserPlus, CheckCircle2 } from "lucide-react";

const steps = [
  {
    icon: UserPlus,
    title: "1. Add a guest",
    body: "Head to the CRM tab and click 'Add guest'. Capture their name, phone, city, and how they heard about your church. Guests start in the 'New' stage.",
  },
  {
    icon: CheckCircle2,
    title: "2. Guest Connection checklist",
    body: "Under Guest Connection you'll see eight simple hospitality steps — welcome video, follow-up text, remembering something personal, etc. Check off any three to satisfy this section. You don't have to do all eight; pick the ones that make sense for that guest.",
  },
  {
    icon: Users,
    title: "3. Track Introduction to the Gospel sessions",
    body: "Open a guest's profile. Under Introduction to the Gospel you'll see the available lessons — pick one to teach (either lesson satisfies the section). Check it off when they attend. Notes for each lesson can be jotted on the right, even before the session happens.",
  },
  {
    icon: Home,
    title: "4. Assign a home study teacher",
    body: "Once the Introduction to the Gospel lesson is complete, the guest moves to 'Needs teacher'. Add teachers under the Teachers tab (name, interests, city). Then, from the guest's profile, click 'Assign' and pick the best fit.",
  },
  {
    icon: GraduationCap,
    title: "5. Home Bible Study → Next Steps",
    body: "The assigned teacher walks the guest through 7 home lessons. When those are done, they move to Next Steps (24 lessons) — usually taught in-class at church. Toggle each as they're completed.",
  },
  {
    icon: CheckCircle2,
    title: "6. Celebrate 'Completed'",
    body: "When all 24 Next Steps lessons are checked, the guest is marked Completed — a fully-established disciple. Their record stays in your CRM as part of your church's story.",
  },
];

export default function CrmHelp() {
  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-12" data-testid="crm-help-page">
      <div className="overline mb-2">Getting started</div>
      <h1 className="font-serif-display text-4xl tracking-tight" style={{ color: "var(--ink)" }}>How the CRM works</h1>
      <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--ink-2)" }}>
        A quick tour of the discipleship pipeline. Move guests from their first visit all the way through Next Steps — one checkbox at a time.
      </p>

      <div className="mt-10 space-y-5">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="card-surface p-6 flex items-start gap-4" data-testid={`crm-help-step-${i + 1}`}>
              <div className="shrink-0 w-10 h-10 flex items-center justify-center" style={{ background: "var(--surface-hover)" }}>
                <Icon size={20} style={{ color: "var(--terracotta)" }} />
              </div>
              <div>
                <h3 className="font-serif-display text-xl leading-tight" style={{ color: "var(--ink)" }}>{s.title}</h3>
                <p className="mt-2 text-sm" style={{ color: "var(--ink-2)" }}>{s.body}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 card-surface p-6 lg:p-8" data-testid="crm-help-tips">
        <h2 className="font-serif-display text-2xl flex items-center gap-2" style={{ color: "var(--ink)" }}>
          <BookOpen size={18} style={{ color: "var(--ember)" }} /> Tips
        </h2>
        <ul className="mt-4 space-y-3 text-sm" style={{ color: "var(--ink-2)" }}>
          <li>· <strong style={{ color: "var(--ink)" }}>Notes live next to every session.</strong> They save as you type — no separate save button.</li>
          <li>· <strong style={{ color: "var(--ink)" }}>Filter by stage.</strong> On the CRM tab, use the stage pills to quickly find guests who need a teacher, are mid-way through, or are close to finishing.</li>
          <li>· <strong style={{ color: "var(--ink)" }}>Search anywhere.</strong> The top search bar looks across names, phones, emails, and cities.</li>
          <li>· <strong style={{ color: "var(--ink)" }}>Only you see your church.</strong> Guests, teachers, and notes are scoped to your organization — other churches on the platform can't see them.</li>
        </ul>
      </div>

      <div className="mt-10 text-center text-sm" style={{ color: "var(--muted)" }}>
        Ready to add someone? <Link to="/crm" className="underline" style={{ color: "var(--terracotta)" }}>Open the CRM →</Link>
      </div>
    </div>
  );
}
